import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Availability } from '@availability/const';
import { ProductAvailabilityFacade } from '@availability/data-access';
import { CustomProduct } from '@product/const';
import { SelectedPriceService } from '@product/data-access';
import { CustomDecimalPipeModule } from '@sdvor/shared/ui';
import { SdvorIconComponent } from '@sdvor/shared/ui/v2';
import { DeviceService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { StateUtils } from '@spartacus/core';
import { filter, map, shareReplay, switchMap } from 'rxjs';

@Component({
  selector: 'sd-product-availability-info-product',
  imports: [SdvorIconComponent, CustomDecimalPipeModule],
  templateUrl: './product-availability-info-product.component.html',
  styleUrls: ['./product-availability-info-product.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductAvailabilityInfoProductComponent {
  private readonly deviceService = inject(DeviceService);
  private readonly productAvailabilityFacade = inject(ProductAvailabilityFacade);
  private readonly selectedPriceService = inject(SelectedPriceService);

  protected readonly product = input.required<CustomProduct>();
  protected readonly forceInStock = input.required<boolean>();
  protected readonly iconTypes = IconTypes;

  protected readonly isDesktopLgUp = toSignal(this.deviceService.isDesktopLgUp$, { initialValue: false });

  private readonly availability$ = toObservable(this.product).pipe(
    filter(Boolean),
    switchMap((product) => this.productAvailabilityFacade.getAvailability(product.code)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly stockLevel = toSignal(
    this.availability$.pipe(
      map((state: StateUtils.LoaderState<Availability>) => state.value?.stockLevel),
      filter(Boolean),
    ),
  );

  readonly stockUnit = toSignal(
    this.availability$.pipe(
      map((state: StateUtils.LoaderState<Availability>) => state.value?.unit.name),
      filter(Boolean),
    ),
  );

  readonly shops = toSignal(
    toObservable(this.product).pipe(
      filter(Boolean),
      switchMap((product) =>
        this.selectedPriceService
          .getSelectedPrice(product.code)
          .pipe(map((price) => ({ product, unitCode: price.unit?.code }))),
      ),
      switchMap(({ product, unitCode }) => this.productAvailabilityFacade.getShopsAvailable(product.code, unitCode)),
      shareReplay({ bufferSize: 1, refCount: true }),
    ),
  );
}
